import { createSeamAxios } from "@seamapi/shared/seam-axios"
import Router from "next/router"
import { useSessionState } from "core/hooks/use-session-state"
import { usePreferredWorkspaceStore } from "core/hooks/use-preferred-workspace-store"
import { addDeniedWorkspaceId } from "core/lib/denied-workspaces"
import toast from "react-hot-toast"
import { type TypedAxios } from "typed-axios-instance"
import type { Routes } from "@seamapi/legacy-react-query/all-routes"
import { attachSessionAuthorization } from "./session-authorization"
import { attachSeamDevToolsRecorder } from "core/hooks/seam-dev-tools-recorder"

export const useUnauthenticatedAxios = () => {
  return createSeamAxios({
    baseURL: process.env.NEXT_PUBLIC_API_HOST,
  })
}

export const getAuthenticatedAxios = (
  workspace_id = ""
): TypedAxios<Routes> => {
  const axios = createSeamAxios({
    baseURL: process.env.NEXT_PUBLIC_API_HOST,
  })

  // Read token at request time, not at instance creation time.
  // This prevents stale tokens when the session is refreshed between
  // hook render and actual request execution.
  attachSessionAuthorization(axios)
  if (workspace_id) {
    axios.interceptors.request.use((config) => {
      config.headers["Seam-Workspace"] = workspace_id
      return config
    })
  }

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error?.response?.status == 401) {
        const isWorkspaceScoped = Boolean(
          error.config?.headers?.["Seam-Workspace"]
        )

        if (isWorkspaceScoped) {
          // Workspace-scoped 401: workspace access denied (or possibly an
          // expired session on a workspace page). Store the denied workspace
          // so the homepage can skip it, preventing a redirect loop. Clear
          // the preferred workspace if it matches. Then do a full page
          // navigation to `/` to clear React Query cache. The homepage will
          // pick the next accessible workspace; if the session is actually
          // expired, the non-workspace useMe() call will hit the
          // session-expiry branch below.
          const deniedId = error.config?.headers?.["Seam-Workspace"] as string
          if (deniedId) {
            addDeniedWorkspaceId(deniedId)
            const preferred =
              usePreferredWorkspaceStore.getState().preferredWorkspaceId
            if (preferred === deniedId) {
              usePreferredWorkspaceStore
                .getState()
                .setPreferredWorkspaceId(null)
            }
          }
          toast.error("Unable to access this workspace. Redirecting...", {
            id: "workspace-access-denied",
          })
          setTimeout(() => {
            window.location.href = "/"
          }, 2000)
        } else {
          // Non-workspace 401: the session token itself is invalid or
          // expired. Clear auth state and re-authenticate via Clerk.
          useSessionState.setState({ isAuthenticated: false })
          toast.error("Session expired. Re-authenticating...", {
            id: "session-expired",
          })
          setTimeout(() => {
            Router.push("/signin-callback")
          }, 2000)
        }
      }
      return Promise.reject(error)
    }
  )

  attachSeamDevToolsRecorder(axios, workspace_id === "" ? null : workspace_id)

  // Cast to any to allow internal routes that aren't in the Routes type

  return axios as any
}

export const useAuthenticatedAxios = (workspace_id?: string) => {
  return getAuthenticatedAxios(workspace_id)
}
